// SCR-004: one customer — the header (name, status, counts, since, id; suspend / restore; billed
// this month) and NAV-038's link tabs, each its own URL (/customers/:orgId/:tab[/:subtab]; the
// Assistant's conversations, NAV-024, at /customers/:orgId/assistant/:sessionId). Every tab,
// Quotas, a quota's detail (its id is the sub-tab segment) and Notifications included, renders
// here under the header and the tab bar (REQ-238). A super-admin sees every tab; a billing
// principal sees Quotas and Billing (GET /v1/admin/orgs/{id} is super-admin only, so its name
// comes from the customer directory and no status action is offered). The routes need only a
// session, so `canOpenTab` is the gate: a tab the session cannot open, reached by URL, shows the
// inline "not available to your role"; another org's page is not found to a customer principal.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi, useCostVisibility, useOrgContext } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { Link, ModelsTab, OrgKeysScreen, NotFound, QuotaDetail, QuotasTab, quotasHref, SignInScreen, UsageTab, useHost, usageDrillFrom, type USAGE_PATH, type USAGE_TEAM_PATH, type USAGE_USER_PATH, type ScopeMe } from "@nexus/ui";
import { GuardrailsScreen, WebhooksTab } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { scopeOf, SessionContext, type Session } from "../../session/session";
import { useCustomerDirectory, useCustomerName } from "../../ui/customer-directory";
import { ConfirmDialog } from "@nexus/ui";
import { HelpButton } from "@nexus/ui";
import { addMonths, currentMonth, monthLabel } from "@nexus/ui";
import { Chip, CopyId, LinkTabs, PageHeader, type TabLink } from "@nexus/ui";
import { useBreadcrumb } from "../../ui/shell";
import { ErrorBanner, Forbidden, Loading } from "@nexus/ui";
import { Money } from "@nexus/ui";
import { BillingTab } from "../terms/billing-tab";
import { AwsAccountsTab } from "./installations";
import { AssistantTab } from "./assistant-tab";
import {
  ASSISTANT_SESSION_PATH,
  awsAccountCount,
  awsAccountCountLabel,
  billedFigure,
  canManageCustomer,
  canOpenTab,
  customerPath,
  customerSinceMonth,
  customerTabLinks,
  firstUsageMonth,
  customerTabFrom,
  defaultTab,
  INSTALLATION_SETTINGS_PATH,
  ORG_DETAIL_PATH,
  ORG_SUBTAB_PATH,
  orgStatusAction,
  orgStatusChip,
  orgStatusConfirmMessage,
  orgStatusToggleRequest,
  TAB_TITLES,
  userCountLabel,
  visibleTabs,
  type BilledFigure,
  type CustomerTab,
  type Org,
} from "./model";
import { InstallationSettings } from "./installation-settings";
import { ActivityBadgedTabs, ActivityTab } from "./activity-tab";
import { Settings } from "./settings";
import { PEOPLE_SUBTABS, peopleSubtab, SubTabs, TeamsScreen, UsersList } from "@nexus/ui";
import type { OrgContext } from "@nexus/ui";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

function useSession(): Session | null {
  const services = useContext(SessionContext);
  return services?.state.status === "signed-in" ? services.state.session : null;
}

/**
 * The tabs a `:subtab` segment means something on (NAV-042): their sub-tabs, or on Quotas a
 * quota's id. Under any other tab the segment is not found, as an unknown sub-tab is.
 */
const TABS_WITH_SUBTABS: ReadonlySet<CustomerTab> = new Set(["people", "sign-in", "keys", "models", "quotas", "activity"]);

interface Counts {
  users: string | null;
  accounts: string | null;
}

const SECTIONS_LABEL = "Customer sections";

/** The customer page's route parameters: a tab or one of its sub-tabs, the Usage tab's explorer level (TASK-252), or an Assistant conversation (NAV-024). */
type CustomerPageParams =
  | ScreenProps<typeof ORG_DETAIL_PATH>["params"]
  | ScreenProps<typeof ORG_SUBTAB_PATH>["params"]
  | ScreenProps<typeof ASSISTANT_SESSION_PATH>["params"]
  | ScreenProps<typeof USAGE_PATH>["params"]
  | ScreenProps<typeof USAGE_TEAM_PATH>["params"]
  | ScreenProps<typeof USAGE_USER_PATH>["params"];

export function CustomerPage({ params, query }: { params: CustomerPageParams; query: URLSearchParams }) {
  const client = useApi();
  const session = useSession();
  const directory = useCustomerDirectory();
  const orgId = params.orgId;
  const assistantSession = "sessionId" in params ? params.sessionId : null;
  const tab = customerTabFrom("tab" in params ? params.tab : assistantSession !== null ? "assistant" : "usage");
  const subtab = "subtab" in params ? params.subtab : undefined;
  const manage = session ? canManageCustomer(session) : false;
  const canBilling = session?.can("billing:read") ?? false;

  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(manage);
  const [error, setError] = useState<ApiError | null>(null);
  const [counts, setCounts] = useState<Counts>({ users: null, accounts: null });
  const [billed, setBilled] = useState<BilledFigure>({ kind: "unknown" });
  const [firstAssigned, setFirstAssigned] = useState<string | null>(null);
  const [firstUsed, setFirstUsed] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const directoryName = useCustomerName(orgId);
  const name = org?.name ?? directoryName;
  // The money policy's input (UI-MONEY-1, REQ-233): a billing principal's, and the customer
  // surface's whatever its permissions, since an org admin's Usage shows money only as its org's
  // cost visibility allows. Until the read answers, or if it fails, the customer sees no money (U4).
  // The surface does not depend on the visibility, so the first context only decides who reads it.
  const surface = useOrgContext(orgId, null)?.surface;
  const costVisibility = useCostVisibility(client, orgId, canBilling || surface === "customer");
  const context = useOrgContext(orgId, costVisibility);

  const load = () => {
    if (!manage) return;
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/orgs/{orgId}", { path: { orgId } })
      .then((loaded) => {
        setOrg(loaded);
        directory.remember([{ id: loaded.id, name: loaded.name }]);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setOrg(null);
    load();
    if (manage) {
      // The meta line's counts: one page of users and the installations (each one call).
      client
        .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { limit: 500 } })
        .then((page) => setCounts((c) => ({ ...c, users: userCountLabel(page.items.length, page.nextCursor !== null) })))
        .catch(() => undefined);
      client
        .get("/v1/orgs/{orgId}/installations", { path: { orgId }, query: { limit: 500 } })
        .then((page) => setCounts((c) => ({ ...c, accounts: awsAccountCountLabel(awsAccountCount(page.items)) })))
        .catch(() => undefined);
    }
    if (canBilling) {
      const month = currentMonth(new Date());
      client
        .get("/v1/admin/revenue/by-org", { query: { from: month, to: month, orgId } })
        .then((report) => setBilled(billedFigure(report, orgId)))
        .catch(() => setBilled({ kind: "unknown" }));
      // "Customer since": the first plan assignment and the first month of usage (three years back).
      client
        .get("/v1/admin/orgs/{orgId}/rate-plan-assignments", { path: { orgId } })
        .then((page) => setFirstAssigned(customerSinceMonth(null, page.items.map((a) => a.effectiveMonth))))
        .catch(() => undefined);
      client
        .get("/v1/admin/revenue/by-month", { query: { from: addMonths(month, -35), to: month, orgId } })
        .then((report) => setFirstUsed(firstUsageMonth(report.items)))
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId, manage, canBilling]);

  const tabTitle = TAB_TITLES[tab];
  const quotaId = tab === "quotas" ? subtab : undefined;
  useBreadcrumb(
    session
      ? [
          { label: name, href: customerPath(orgId, defaultTab(session, orgId)) },
          quotaId ? { label: tabTitle, href: customerPath(orgId, tab) } : { label: tabTitle },
          ...(quotaId ? [{ label: "Quota" }] : []),
        ]
      : null,
  );

  if (!session) return null;
  // A customer principal never sees another org's page, not even its frame (the org context refuses it too).
  if (session.me.orgId !== null && session.me.orgId !== orgId) return <NotFound />;
  // Nothing to open here: no header either, whose name would fall back to the org's id (IA-1).
  if (visibleTabs(session, orgId).length === 0) return <Forbidden what="This customer" />;
  if (loading) return <Loading label="Loading customer…" />;
  if (error) return <ErrorBanner error={error} onRetry={load} />;

  const me = scopeOf(session.me);
  const sinceMonthValue = firstAssigned || firstUsed ? customerSinceMonth(null, [firstAssigned, firstUsed]) : customerSinceMonth(org?.createdAt, []);
  const action = org ? orgStatusAction(org) : null;
  const chip = org ? orgStatusChip(org.status) : null;

  const confirmStatusChange = () => {
    if (!action) return;
    setBusy(true);
    setActionError(null);
    client
      .patch("/v1/admin/orgs/{orgId}", { path: { orgId }, body: orgStatusToggleRequest(action) })
      .then((updated) => {
        setOrg(updated);
        setConfirming(false);
      })
      .catch((err: unknown) => {
        setActionError(unexpected(err));
        setConfirming(false);
      })
      .finally(() => setBusy(false));
  };

  const tabs = customerTabLinks(session, orgId);
  const sections = canOpenTab(session, "activity", orgId) ? <ActivityBadgedTabs orgId={orgId} tabs={tabs} current={tab} label={SECTIONS_LABEL} /> : <LinkTabs tabs={tabs} current={tab} label={SECTIONS_LABEL} />;

  return (
    <div class="page customer-page">
      <PageHeader
        title={name}
        titleExtra={chip && <Chip tone={chip.tone}>{chip.label}</Chip>}
        meta={
          <>
            {counts.users && <span>{counts.users}</span>}
            {counts.accounts && <span>{counts.accounts}</span>}
            {sinceMonthValue && <span>Customer since {monthLabel(sinceMonthValue)}</span>}
            <CopyId id={orgId} what="customer ID" />
          </>
        }
        actions={
          <>
            {action && (
              <button type="button" class={action.next === "suspended" ? "button button-danger" : "button"} onClick={() => setConfirming(true)}>
                {action.label}
              </button>
            )}
            {canBilling && billed.kind !== "unknown" && (
              <div class="header-figure">
                <span class="header-figure-label">
                  Billed this month
                  <HelpButton topic="customers.billedThisMonth" label="Billed this month" />
                </span>
                <span class="header-figure-value">{billed.kind === "billed" ? <Money micro={billed.micro} /> : "Not billed"}</span>
              </div>
            )}
          </>
        }
      />

      {actionError && <ErrorBanner error={actionError} />}

      {sections}

      {!canOpenTab(session, tab, orgId) ? (
        <Forbidden what={`The ${tabTitle} tab`} />
      ) : subtab !== undefined && !TABS_WITH_SUBTABS.has(tab) ? (
        <div class="tab-panel">
          <NotFound />
        </div>
      ) : (
        <div class="tab-panel">
          {tab === "usage" && context && <UsageTab context={context} drill={usageDrillFrom(params)} query={query} />}
          {tab === "people" && context && (
            // People's sub-tabs on the staff surface (REQ-241): Users · Teams; staff invite per installation (SCR-583).
            <>
              <SubTabs tabs={PEOPLE_SUBTABS} scope="staff" current={subtab} href={(id) => customerPath(orgId, "people", id)} label="People" />
              {peopleSubtab(subtab, "staff") === "users" && <UsersList context={context} />}
              {peopleSubtab(subtab, "staff") === "teams" && <TeamsScreen context={context} me={me} />}
              {peopleSubtab(subtab, "staff") === null && <NotFound />}
            </>
          )}
          {tab === "aws-accounts" && <AwsAccountsTab orgId={orgId} canInstall={session.can("installer:run")} />}
          {tab === "sign-in" && context && <SignInScreen context={context} scope="org" subtab={subtab} subtabHref={(id) => customerPath(orgId, "sign-in", id)} />}
          {tab === "keys" && context && (
            <OrgKeysScreen context={context} scope="org" subtab={subtab} subtabHref={(id) => customerPath(orgId, "keys", id)} installationsHref={customerPath(orgId, "aws-accounts")} />
          )}
          {tab === "models" && context && <ModelsTab context={context} scope="org" subtab={subtab} subtabHref={(id) => customerPath(orgId, "models", id)} />}
          {tab === "guardrails" && context && <GuardrailsScreen context={context} />}
          {tab === "quotas" && context && <QuotasPanel context={context} me={me} quotaId={quotaId} />}
          {tab === "notifications" && context && <WebhooksTab context={context} me={me} />}
          {tab === "activity" && <ActivityTab orgId={orgId} session={session} me={me} subtab={subtab} context={context} query={query} />}
          {tab === "billing" && <BillingTab orgId={orgId} customer={name} />}
          {tab === "settings" && <Settings orgId={orgId} />}
          {tab === "assistant" && <AssistantTab orgId={orgId} sessionId={assistantSession} query={query} />}
        </div>
      )}

      {action && org && (
        <ConfirmDialog
          title={`${action.label} customer`}
          open={confirming}
          message={orgStatusConfirmMessage(org, action)}
          confirmLabel={action.label}
          typedName={org.name}
          busy={busy}
          onConfirm={confirmStatusChange}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/** The Quotas tab: the org's quotas, or one quota's standing, grants and history with the way back. */
function QuotasPanel({ context, me, quotaId }: { context: OrgContext; me: ScopeMe; quotaId: string | undefined }) {
  const host = useHost();
  if (!quotaId) return <QuotasTab context={context} me={me} />;
  return (
    <>
      <p>
        <Link href={quotasHref(host, context.orgId)}>All quotas</Link>
      </p>
      <QuotaDetail context={context} me={me} quotaId={quotaId} />
    </>
  );
}

/** NAV-014's installation settings: the overview under this customer, one installation's detail
 *  under the scope selector. Super-admin only (the route's permission). */
export function InstallationSettingsPage({ params }: ScreenProps<typeof INSTALLATION_SETTINGS_PATH>) {
  return <InstallationSettings orgId={params.orgId} />;
}
